import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import GOOGLE_MAPS_CONFIG from '../config/googleMaps';

// Lazy Google Maps loader.
//
// Previously this provider called `useLoadScript` unconditionally, so the full
// Google Maps JS API (several hundred KB) downloaded on EVERY page for EVERY
// visitor — home page included — even though maps are only used on delivery,
// checkout, and admin screens.
//
// Now the script loads on demand: the external script is only fetched once the
// first maps-consuming component mounts (they all call `useGoogleMapsContext()`,
// which flags the need). Non-map pages never pay the cost.

const GoogleMapsContext = createContext(null);

export const useGoogleMapsContext = () => {
  const ctx = useContext(GoogleMapsContext);
  // Signal that this subtree needs maps. Safe to call every render — the
  // request flag only ever flips false → true once.
  useEffect(() => { ctx?.requestLoad?.(); }, [ctx]);
  return ctx;
};

// Inner component so `useLoadScript` (which starts the download on mount) only
// mounts once something actually asked for maps.
const ScriptLoader = ({ onState }) => {
  const { isLoaded, loadError } = useLoadScript({
    id: 'google-maps-script',
    googleMapsApiKey: GOOGLE_MAPS_CONFIG.apiKey,
    libraries: GOOGLE_MAPS_CONFIG.libraries,
    region: 'ZW',
    language: 'en',
    version: 'weekly',
  });
  useEffect(() => { onState({ isLoaded, loadError }); }, [isLoaded, loadError, onState]);
  return null;
};

const GoogleMapsProvider = ({ children }) => {
  const [wanted, setWanted] = useState(false);
  const [state, setState] = useState({ isLoaded: false, loadError: null });
  const requestLoad = useCallback(() => setWanted(true), []);

  return (
    <GoogleMapsContext.Provider value={{ ...state, requestLoad }}>
      {wanted && <ScriptLoader onState={setState} />}
      {children}
    </GoogleMapsContext.Provider>
  );
};

export default GoogleMapsProvider;
